// ── GQ AI Tool Registry ───────────────────────────────────────────────
// Each tool has:
//   - name           identifier the AI emits in <action name="…"/>
//   - description    human + AI-readable summary (used in system prompt)
//   - schema         args the AI is allowed to pass
//   - confirm        true → user must approve before execute() runs
//   - execute(args)  the actual work, returns a string summary
//
// Tools run in the browser. Persistence (when needed) goes through window.GQ.state.

(function (root) {
  const GQ = root.GQ = root.GQ || {};
  const tools = {};

  // ────────────────────────────────────────────────────────────────────
  // Rate limit — configurable tool executions / rolling hour (default 20, override via VITE_AI_RATE_LIMIT)
  // ────────────────────────────────────────────────────────────────────
  const RATE_KEY = 'gq.ai.toolExecs';
  const RATE_LIMIT = (typeof import.meta !== 'undefined' && +import.meta.env?.VITE_AI_RATE_LIMIT) || 20;
  function recordExec() {
    const now = Date.now();
    const recent = (JSON.parse(localStorage.getItem(RATE_KEY) || '[]'))
      .filter(t => now - t < 3600000);
    recent.push(now);
    localStorage.setItem(RATE_KEY, JSON.stringify(recent));
    return recent.length;
  }
  function rateRemaining() {
    const now = Date.now();
    const recent = (JSON.parse(localStorage.getItem(RATE_KEY) || '[]'))
      .filter(t => now - t < 3600000);
    return Math.max(0, RATE_LIMIT - recent.length);
  }

  // ────────────────────────────────────────────────────────────────────
  // Strategy resolution helper — accepts an id, a name fragment, or null
  // (which means "the current strategy"). Returns the row or null.
  // ────────────────────────────────────────────────────────────────────
  async function resolveStrategy(idOrName) {
    const state = GQ.state;
    if (!idOrName) return state?.getCurrentStrategy() || null;
    // Try cache first
    if (state?.mem?.strategies?.[idOrName]) return state.mem.strategies[idOrName];
    // Lookup in Supabase
    const sb = root.sbClient;
    if (!sb) return null;
    const { data: { user } } = await sb.auth.getUser();
    if (!user) return null;
    // Try by id (uuid), then by name match
    let q = sb.from('strategies').select('*').eq('user_id', user.id);
    const isUuid = /^[0-9a-f-]{36}$/i.test(idOrName);
    if (isUuid) q = q.eq('id', idOrName);
    else        q = q.ilike('name', `%${idOrName}%`);
    const { data } = await q.limit(1).maybeSingle();
    return data || null;
  }

  // ────────────────────────────────────────────────────────────────────
  // Default strategy spec — used when a strategy has no `current_params`
  // ────────────────────────────────────────────────────────────────────
  function defaultSpec(p = {}) {
    return {
      indicators: [
        { id:'ef',    type:'ema', source:'close', params:{ period: p.fast  || 9  } },
        { id:'es',    type:'ema', source:'close', params:{ period: p.slow  || 21 } },
        { id:'rsi14', type:'rsi', source:'close', params:{ period: p.rsiP  || 14 } },
        { id:'atr14', type:'atr', source:'close', params:{ period: 14 } },
      ],
      entry:      { long: [`ef > es and rsi14 > ${p.rsiT || 50}`] },
      exit:       { long: ['ef < es'] },
      sizing:     { mode: 'atrTarget', atrK: 2, riskPct: (p.stopL || 1) / 100 },
      execution:  { orderType: 'market' },
      costs:      { commissionBps: 5, slippageBps: 2, slippageModel: 'volImpact' },
      constraints:{ allowShort: false },
    };
  }

  // ────────────────────────────────────────────────────────────────────
  // Tools — read-only / instant (no confirmation needed)
  // ────────────────────────────────────────────────────────────────────
  tools.navigate = {
    description: 'Navigate the user to a different page. Optional run id deep-links to a specific result.',
    schema: { to: 'string (vault|backtest|optimize|stress|tearsheet|compare|strategy-detail|marketplace|designer|settings|mint)', run: 'optional uuid' },
    confirm: false,
    async execute({ to, run }) {
      if (!to) return 'Navigation failed (no target).';
      root.navigateTo(to, run ? { run } : {});
      return `Navigated to ${to}${run ? ' with run ' + run.slice(0,8) : ''}.`;
    },
  };

  tools.listStrategies = {
    description: 'List the user’s saved strategies.',
    schema: {},
    confirm: false,
    async execute() {
      const sb = root.sbClient;
      if (!sb) return 'Supabase unavailable.';
      const { data: { user } } = await sb.auth.getUser();
      if (!user) return 'Not signed in.';
      const { data } = await sb.from('strategies').select('id,name,market,strategy_type,created_at')
        .eq('user_id', user.id).order('created_at',{ascending:false}).limit(20);
      if (!data || !data.length) return 'No strategies yet — mint one first.';
      return data.map(s => `${s.id.slice(0,8)} · ${s.name} (${s.market || '—'})`).join('\n');
    },
  };

  tools.setCurrentStrategy = {
    description: 'Set the active strategy so other tools and pages reference it.',
    schema: { strategyId: 'uuid OR name fragment' },
    confirm: false,
    async execute({ strategyId }) {
      const s = await resolveStrategy(strategyId);
      if (!s) return `No strategy matches "${strategyId}".`;
      GQ.state?.setCurrentStrategy(s);
      return `Active strategy → "${s.name}".`;
    },
  };

  tools.fetchRun = {
    description: 'Fetch the metrics of a previous run (backtest / optimization / stress) by id.',
    schema: { runId: 'uuid', kind: 'optional: backtest|optimization|stress' },
    confirm: false,
    async execute({ runId, kind }) {
      const state = GQ.state;
      if (!state || !runId) return 'Run id required.';
      const r = kind === 'optimization' ? await state.fetchOptimizationRun(runId)
              : kind === 'stress'       ? await state.fetchStressRun(runId)
              :                            await state.fetchBacktestRun(runId);
      if (!r) return `Run ${runId.slice(0,8)} not found.`;
      const m = r.metrics || r.best_metrics || r.risk_metrics || {};
      const bits = [
        m.sharpe       != null && `Sharpe ${m.sharpe.toFixed(2)}`,
        m.maxDrawdown  != null && `MaxDD ${(m.maxDrawdown*100).toFixed(1)}%`,
        m.totalReturn  != null && `Return ${(m.totalReturn*100).toFixed(1)}%`,
        m.winRate      != null && `Win ${(m.winRate*100).toFixed(0)}%`,
      ].filter(Boolean);
      return `Run ${runId.slice(0,8)} → ${bits.join(' · ') || 'no metrics'}.`;
    },
  };

  tools.explainMetric = {
    description: 'Explain a quant metric in one sentence.',
    schema: { metric: 'string (sharpe|sortino|calmar|maxdd|var|cvar|pbo|dsr)' },
    confirm: false,
    async execute({ metric }) {
      const M = {
        sharpe:  'Sharpe: annualised excess return / total volatility. >1 OK, >2 excellent.',
        sortino: 'Sortino: like Sharpe but only counts downside vol. Better for asymmetric returns.',
        calmar:  'Calmar: CAGR / |Max Drawdown|. Return per unit of worst loss.',
        maxdd:   'Max Drawdown: largest peak-to-trough loss. Critical capital risk metric.',
        var:     'VaR: loss that should not be exceeded with given confidence (e.g. VaR95 = 5%-tail).',
        cvar:    'CVaR / Expected Shortfall: average loss when in the worst α% of cases.',
        pbo:     'Probability of Backtest Overfitting: how likely an in-sample winner ranks below median OOS.',
        dsr:     'Deflated Sharpe Ratio: adjusts SR for selection bias when many strategies were tried.',
      };
      return M[(metric || '').toLowerCase()] || `No explanation indexed for "${metric}".`;
    },
  };

  // ────────────────────────────────────────────────────────────────────
  // Tools — engine-executing (confirmation required)
  // ────────────────────────────────────────────────────────────────────
  tools.runBacktestNow = {
    description: 'Run a backtest now using the bundled engine and save the result. Streams progress.',
    schema: { strategyId: 'optional uuid', asset: 'string', from: 'YYYY-MM-DD', to: 'YYYY-MM-DD', capital: 'number' },
    confirm: true,
    async execute({ strategyId, asset = 'SPY', from = '2023-01-01', to = '2024-12-31', capital = 10000 }, onProgress) {
      const state = GQ.state;
      if (!GQ.engine || !GQ.data || !state) return 'Engine/data libs not loaded.';
      onProgress?.('Loading bars…');
      const bars = await GQ.data.load(asset, { bars: 1260 });
      const slice = GQ.data.sliceByDate(bars, from, to);
      const useBars = slice.length > 30 ? slice : bars;
      const strat = strategyId ? (await resolveStrategy(strategyId)) : state.getCurrentStrategy();
      const spec = strat?.config_json || strat?.current_params ? defaultSpec(strat.current_params) : defaultSpec();
      onProgress?.(`Running engine on ${useBars.length} bars…`);
      const r = GQ.engine.run(spec, useBars, { capital: +capital, seed: 42 });
      const run = await state.saveBacktestRun({
        strategy_id: strat?.id,
        asset, timeframe: '1d',
        date_start: useBars[0].time, date_end: useBars[useBars.length-1].time,
        capital, fees_bps: 5, slippage_bps: 2,
        config: spec, metrics: r.metrics, trades: r.trades,
        equity_curve: r.equity_curve,
      });
      const m = r.metrics;
      return `Backtest ${run.id.slice(0,8)} done · Sharpe ${m.sharpe.toFixed(2)} · MaxDD ${(m.maxDrawdown*100).toFixed(1)}% · ${r.trades.length} trades.`;
    },
  };

  tools.runOptimizationNow = {
    description: 'Run a genetic / Bayesian parameter optimization on the current strategy.',
    schema: { algorithm: 'GA|Bayesian', objective: 'sharpe|sortino|cagr', generations: 'number (default 20)', strategyId: 'optional uuid' },
    confirm: true,
    async execute({ algorithm = 'GA', objective = 'sharpe', generations = 20, strategyId }, onProgress) {
      const { engine, data, state, optimize } = GQ;
      if (!engine || !optimize || !state) return 'Optimize lib not loaded.';
      const bars = await data.load('SPY', { bars: 1260 });
      const ranges = {
        fast: [5, 15, 'int'], slow: [20, 60, 'int'], rsiP: [7, 21, 'int'],
        rsiT: [40, 60, 'int'], stopL: [0.5, 3.0, 'float'],
      };
      const fitness = async (p) => {
        if (p.fast >= p.slow) return -10;
        const r = engine.run(defaultSpec(p), bars, { capital: 10000, seed: 42 });
        return r.metrics[objective] || 0;
      };
      onProgress?.(`Starting ${algorithm} optimization · ${generations} generations…`);
      let result;
      if (algorithm === 'Bayesian') {
        result = await optimize.bayesianOptimize({
          ranges, fitness, iterations: generations, initialPoints: 6, seed: 42,
          onIteration: ({ iter }) => onProgress?.(`Iteration ${iter+1}/${generations}…`),
        });
      } else {
        result = await optimize.geneticAlgorithm({
          ranges, fitness, generations, populationSize: 12, seed: 42,
          onGeneration: ({ gen }) => onProgress?.(`Generation ${gen+1}/${generations}…`),
        });
      }
      const strat = strategyId ? (await resolveStrategy(strategyId)) : state.getCurrentStrategy();
      const final = engine.run(defaultSpec(result.bestParams), bars, { capital: 10000, seed: 42 });
      const run = await state.saveOptimizationRun({
        strategy_id: strat?.id,
        objective, generations, population_size: 12,
        best_params: result.bestParams,
        best_metrics: { bestFitness: result.bestFitness, ...final.metrics },
        history: result.history || [],
      });
      const bp = Object.entries(result.bestParams).map(([k,v]) => `${k}=${v}`).join(', ');
      return `Optimization ${run.id.slice(0,8)} done · best ${objective} ${result.bestFitness.toFixed(2)} · ${bp}.`;
    },
  };

  tools.runStressNow = {
    description: 'Run the stress test suite (Monte Carlo, scenarios, factor attribution) on the current strategy.',
    schema: { strategyId: 'optional uuid', scenarios: 'optional comma-separated keys' },
    confirm: true,
    async execute({ strategyId, scenarios }, onProgress) {
      const { engine, data, stress, stats, state } = GQ;
      if (!stress || !engine) return 'Stress lib not loaded.';
      onProgress?.('Loading bars…');
      const bars = await data.load('SPY', { bars: 1260 });
      const strat = strategyId ? (await resolveStrategy(strategyId)) : state.getCurrentStrategy();
      const spec = strat?.current_params ? defaultSpec(strat.current_params) : defaultSpec();
      onProgress?.('Running base backtest…');
      const base = engine.run(spec, bars, { capital: 10000, seed: 42 });
      const rets = stats.equityToReturns(base.equity_curve.map(p => p.v));
      const scen = scenarios ? scenarios.split(',').map(s=>s.trim()) : ['vol2x','rate200bp','blackSwan25'];
      onProgress?.(`Running ${scen.length} scenarios + 1k MC sims…`);
      const suite = stress.runStressSuite({
        strategyReturns: rets, scenarios: scen,
        mcOpts: { horizon: 252, nSims: 1000, method: 'block', blockSize: 20 }, seed: 1,
      });
      const run = await state.saveStressRun({
        strategy_id: strat?.id,
        optimization_run_id: state.getLastOptimizationRunId(),
        scenarios: scen, results: suite.scenarios,
        mc_paths: { bands: suite.monteCarlo.bands, stats: suite.monteCarlo.stats },
        risk_metrics: suite.tail, regime_analysis: suite.regime,
        factor_attribution: suite.factor || null,
      });
      const t = suite.tail || {};
      return `Stress ${run.id.slice(0,8)} done · score ${suite.score}/100 · VaR99 ${(t.var99_hist*100).toFixed(2)}% · ${scen.length} scenarios.`;
    },
  };

  // ────────────────────────────────────────────────────────────────────
  // Public surface
  // ────────────────────────────────────────────────────────────────────
  GQ.aiTools = {
    tools,
    list: () => Object.keys(tools),
    get:  (name) => tools[name],
    rateRemaining,
    rateLimit: () => RATE_LIMIT,
    recordExec,
    // Build the AVAILABLE ACTIONS block for the system prompt
    promptHelp: () => Object.entries(tools)
      .map(([name, t]) => `  <action name="${name}"${Object.keys(t.schema).map(k => ` ${k}="…"`).join('')} />  // ${t.description}`)
      .join('\n'),
  };
})(typeof window !== 'undefined' ? window : globalThis);
